import React from 'react';
import { Link } from 'react-router-dom';
import MkBtn from './MkBtn';
import bmbIcon from '../../assets/images/mk/mk-bmb-icon-192.webp';
import founderPhoto from '../../marketing/founderPhoto';
import { MK_FLAGS } from '../../marketing/flags';
import { FOUNDER_NOTE } from '../../marketing/founderNote';
import { BMB_IOS_URL, bmbPlayHref } from '../../marketing/links';
import { mkTrack } from '../../marketing/track';

const kmoLogo = require('../../assets/images/logo-icon.svg').default;

/**
 * The founder's note (spec §5.6): a dark-paper letter mirroring the in-app dialog. When the owner
 * gate (G1) is off — or there is no photo — it renders signed by the team with a brand medallion.
 * variant: 'full' (Home, with CTA row) | 'compact' (Bring Me Back page, links to the full note).
 */
const MkFounderNote = ({
  variant = 'full',
  platform,
  placement = 'home',
  approved = MK_FLAGS.FOUNDER_NOTE_APPROVED && !!founderPhoto,
  photo = founderPhoto,
}) => {
  const full = variant !== 'compact';
  const signed = !!approved && !!photo;
  const titleId = full ? 'mk-founder-title' : 'mk-founder-compact-title';
  let title = signed ? FOUNDER_NOTE.title : FOUNDER_NOTE.fallbackTitle;
  if (!full) title = FOUNDER_NOTE.compactTitle;
  const photoW = full ? 112 : 80;
  const photoH = full ? 138 : 98;

  let cta = null;
  if (full) {
    if (platform === 'android') {
      cta = (
        <MkBtn
          variant="grad"
          className="mk-letter__btn"
          href={bmbPlayHref(`${placement}_founder`)}
          onClick={() => mkTrack('founder_cta_click', `play:${placement}`)}
        >
          Get Bring Me Back<span className="screen-reader"> on Google Play</span>
        </MkBtn>
      );
    } else if (platform === 'ios') {
      cta = (
        <MkBtn
          variant="grad"
          className="mk-letter__btn"
          href={BMB_IOS_URL}
          onClick={() => mkTrack('founder_cta_click', `appstore:${placement}`)}
        >
          Get Bring Me Back<span className="screen-reader"> on the App Store</span>
        </MkBtn>
      );
    } else {
      cta = (
        <MkBtn
          variant="grad"
          className="mk-letter__btn"
          to="/bring-me-back"
          onClick={() => mkTrack('founder_cta_click', `page:${placement}`)}
        >
          Get Bring Me Back<span className="screen-reader"> — see download options</span>
        </MkBtn>
      );
    }
  }

  return (
    <article
      className={`mk-letter mk-letter--${full ? 'full' : 'compact'} reveal-from-bottom`}
      data-reveal-offset="120"
      aria-labelledby={titleId}
    >
      <p className="mk-letter__kicker">{FOUNDER_NOTE.kicker}</p>
      <h2 id={titleId} className="mk-letter__title">
        {title}
      </h2>
      <div className="mk-letter__rule" aria-hidden="true" />
      <div className="mk-letter__wrap">
        {signed ? (
          <figure className="mk-letter__photo">
            <img
              src={photo}
              width={photoW}
              height={photoH}
              alt={FOUNDER_NOTE.photoAlt}
              loading="lazy"
              decoding="async"
            />
          </figure>
        ) : (
          <div className="mk-letter__photo mk-letter__photo--mark" aria-hidden="true">
            <span className="mk-letter__medallion">
              <img className="mk-letter__medallion-kmo" src={kmoLogo} alt="" width={40} height={40} />
              <img
                className="mk-letter__medallion-bmb"
                src={bmbIcon}
                alt=""
                width={40}
                height={40}
                loading="lazy"
                decoding="async"
              />
            </span>
          </div>
        )}
        <p className="mk-letter__body">{FOUNDER_NOTE.body}</p>
      </div>
      <ul className="list-reset mk-letter__bullets">
        {FOUNDER_NOTE.bullets.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
      <p className="mk-letter__sig">{signed ? FOUNDER_NOTE.signature : FOUNDER_NOTE.fallbackSignature}</p>
      {full && <p className="mk-letter__foot">{FOUNDER_NOTE.footnote}</p>}
      {full && <div className="mk-letter__hair" aria-hidden="true" />}
      {full && (
        <div className="mk-letter__cta">
          <img className="mk-letter__cta-icon" src={bmbIcon} alt="" width={48} height={48} loading="lazy" decoding="async" />
          {cta}
        </div>
      )}
      <p className="mk-letter__more">
        {full ? (
          <a href="#bring-me-back" onClick={() => mkTrack('cta_click', `bmb_anchor:${placement}_founder`)}>
            See what Bring Me Back does →
          </a>
        ) : (
          <Link to="/#founder-note" onClick={() => mkTrack('cta_click', 'home_founder_note:bmb')}>
            Read the full note →
          </Link>
        )}
      </p>
    </article>
  );
};

export default MkFounderNote;
